import { Segment } from '@app/models/micro/segment.model';
import { getLegEndpoints } from './leg-endpoints.utils';

/**
 * Filtra los travelDocuments devueltos por el backend para incluir únicamente
 * los que corresponden a segmentos con windowSegmentStatus === 'CKI'.
 *
 * Soporta hidden stops: cuando un documento contiene los endpoints del leg completo
 * (origen → destino final) en lugar de un segmento individual, también se incluye.
 *
 * @param travelDocuments - Array de documentos de viaje (pectab strings) del pasajero
 * @param segments - Segmentos del flightSummary de la reservación
 * @returns Array de documentos filtrados por segmentos CKI; si no hay CKI, devuelve todos
 */
export function getTravelDocumentsForCkiSegments(
  travelDocuments: string[],
  segments: Segment[]
): string[] {
  if (!travelDocuments?.length) return [];

  const ckiSegments = segments?.filter((s) => s.windowSegmentStatus === 'CKI') || [];

  if (ckiSegments.length === 0) return travelDocuments;

  const legEndpoints = getLegEndpoints(ckiSegments);

  return travelDocuments.filter((doc) => {
    const matchesSegment = ckiSegments.some(
      (seg) => doc.includes(seg.departureAirport) && doc.includes(seg.arrivalAirport)
    );

    if (matchesSegment) return true;

    if (!legEndpoints) return false;

    return doc.includes(legEndpoints.origin) && doc.includes(legEndpoints.destination);
  });
}

/**
 * Extrae el identificador único de un travelDocument para detectar documentos
 * que corresponden al mismo pasajero/segmento aunque su contenido haya cambiado
 * (por ejemplo, por un cambio de asiento que modifica la zona de embarque).
 *
 * Usa el ticket number (^I3) + aeropuertos de salida/llegada (^60 y ^70) como clave.
 * Si no puede extraer estos campos, retorna null.
 */
function getDocSegmentKey(doc: string): string | null {
  const ticketMatch = doc.match(/\^I3([^^]*)/);
  const depMatch = doc.match(/\^60([^^]*)/);
  const arrMatch = doc.match(/\^70([^^]*)/);

  if (ticketMatch && depMatch && arrMatch) {
    return `${ticketMatch[1].trim()}|${depMatch[1].trim()}|${arrMatch[1].trim()}`;
  }
  return null;
}

/**
 * Acumula travelDocuments en el pasajero sin duplicar documentos ya existentes.
 *
 * El backend devuelve el mismo pasajero múltiples veces cuando hay conexiones
 * con split de peticiones (una entrada por segmento). Sin acumulación, cada
 * iteración sobreescribe los docs anteriores y solo queda el último segmento.
 *
 * Cuando un documento nuevo corresponde al mismo segmento que uno existente
 * (mismo ticket + misma ruta), el nuevo reemplaza al viejo. Esto evita
 * duplicaciones cuando el backend retorna un documento actualizado (ej: zona
 * de embarque recalculada tras un cambio de asiento).
 *
 * @param existingDocs - Array actual de travelDocuments del pasajero
 * @param newDocs - Nuevos documentos a agregar
 * @param segments - Segmentos del flightSummary para filtrar por CKI
 * @returns Array combinado sin duplicados por segmento
 */
export function accumulateTravelDocuments(
  existingDocs: string[],
  newDocs: string[],
  segments: Segment[]
): string[] {
  const filtered = getTravelDocumentsForCkiSegments(newDocs, segments);
  if (!filtered.length) return existingDocs || [];

  // Construir mapa de claves de los nuevos documentos para detectar reemplazos
  const newDocKeys = new Map<string, string>();
  for (const doc of filtered) {
    const key = getDocSegmentKey(doc);
    if (key) {
      newDocKeys.set(key, doc);
    }
  }

  // Filtrar existingDocs: remover los que serán reemplazados por un nuevo doc del mismo segmento
  const combined = (existingDocs || []).filter((existingDoc) => {
    const key = getDocSegmentKey(existingDoc);
    // Si el doc existente tiene la misma clave que un nuevo doc, se remueve (será reemplazado)
    return !key || !newDocKeys.has(key);
  });

  // Agregar los nuevos documentos (sin duplicar por string exacto como fallback)
  for (const doc of filtered) {
    if (!combined.includes(doc)) {
      combined.push(doc);
    }
  }
  return combined;
}
